import { useState } from 'react'
import { Box, Button, Collapse, HStack, Link, SimpleGrid, Text, VStack, Wrap, WrapItem } from '@chakra-ui/react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { formatWallClockDateTime } from '@/lib/utils/dateTimeUtils'
import { RequestSourceBadge } from '@/components/helpdesk/Cell/RequestSourceBadge'
import type { RequestOption } from '@/types/ticket'

type Props = {
  request: RequestOption
  formatDate: (iso: string) => string
  defaultOpen?: boolean
}

function DetailCell({ label, children }: { label: string; children: React.ReactNode }) {
  const colors = useThemeColors()
  return (
    <Box>
      <Text fontSize="11px" fontWeight="500" color={colors.textSecondary}>
        {label}
      </Text>
      <Box fontSize="12px" color={colors.text} mt="2px">
        {children}
      </Box>
    </Box>
  )
}

function buildSummaryLine(request: RequestOption): string {
  const parts: string[] = []
  const orgOrClient = request.clientLabel || request.clientOrg || request.clientName
  if (orgOrClient) parts.push(orgOrClient)
  if (request.contactName) parts.push(request.contactName)
  if (request.contactNumber) parts.push(request.contactNumber)
  if (request.siteAddress) parts.push(request.siteAddress)
  if (request.serialNumber) parts.push(`SN: ${request.serialNumber}`)
  return parts.join(' · ')
}

function formatScheduleRange(
  request: RequestOption,
): string | null {
  if (request.preferredScheduleAt) {
    const start = formatWallClockDateTime(request.preferredScheduleAt)
    const end = request.preferredScheduleUntil
      ? formatWallClockDateTime(request.preferredScheduleUntil)
      : null
    return end && end !== '—' ? `${start} to ${end}` : start
  }
  if (request.contactAvailabilityText) {
    return request.contactAvailabilityText
  }
  return null
}

export default function RequestDetailsCollapse({ request, formatDate, defaultOpen = false }: Props) {
  const colors = useThemeColors()
  const [open, setOpen] = useState(defaultOpen)

  const issueText = request.issueDescription?.trim()
  const descText = request.description?.trim()
  const showIssue = issueText && issueText !== descText
  const summaryLine = buildSummaryLine(request)
  const scheduleRange = formatScheduleRange(request)
  const hasCapturedDetails = Boolean(
    request.contactNumber
      || request.siteAddress
      || request.preferredScheduleAt
      || request.preferredScheduleUntil
      || request.contactAvailabilityText
      || request.clientLabel
      || request.serialNumber
      || request.unitModel,
  )

  return (
    <Box
      mt={2}
      p="8px 10px"
      bg={colors.sectionBg ?? 'gray.50'}
      borderRadius="md"
    >
      <HStack justify="space-between" align="flex-start" spacing={2}>
        <Box minW={0} flex="1">
          <Text
            fontSize="10px"
            fontWeight="500"
            color={colors.textMuted}
            textTransform="uppercase"
            letterSpacing="0.03em"
            mb="2px"
          >
            Linked request
          </Text>
          <Text fontSize="sm" fontWeight="500" color={colors.text} noOfLines={1}>
            {request.id}
          </Text>
          {summaryLine ? (
            <Text fontSize="12px" color={colors.textSecondary} mt="2px" noOfLines={2}>
              {summaryLine}
            </Text>
          ) : null}
          {!open && scheduleRange ? (
            <Text fontSize="11px" color={colors.textMuted} mt="4px" noOfLines={1}>
              Schedule: {scheduleRange}
            </Text>
          ) : null}
        </Box>
        <Button
          size="xs"
          variant="ghost"
          fontSize="12px"
          px="10px"
          py="4px"
          h="auto"
          minH="auto"
          leftIcon={open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          onClick={() => setOpen((v) => !v)}
          flexShrink={0}
        >
          {open ? 'Hide' : 'Details'}
        </Button>
      </HStack>
      <Collapse in={open} animateOpacity>
        <VStack align="stretch" spacing={2} mt="10px" pt="10px" borderTop="0.5px solid" borderColor={colors.borderLight}>
          {showIssue ? (
            <Box mb={2}>
              <Text fontSize="11px" fontWeight="500" color={colors.textSecondary}>
                Issue description
              </Text>
              <Text fontSize="12px" color={colors.text} whiteSpace="pre-wrap" mt="2px">
                {issueText}
              </Text>
            </Box>
          ) : null}
          {!showIssue && descText ? (
            <Box mb={2}>
              <Text fontSize="11px" fontWeight="500" color={colors.textSecondary}>
                Issue description
              </Text>
              <Text fontSize="12px" color={colors.text} whiteSpace="pre-wrap" mt="2px">
                {descText}
              </Text>
            </Box>
          ) : null}
          {request.categories && request.categories.length > 0 ? (
            <Box mb={2}>
              <Text fontSize="11px" fontWeight="500" color={colors.textSecondary} mb={1}>
                Categories
              </Text>
              <Wrap spacing="6px">
                {request.categories.map((cat, idx) => (
                  <WrapItem key={idx}>
                    <Box
                      px={2}
                      py="2px"
                      bg="blue.50"
                      color="blue.700"
                      borderRadius="md"
                      fontSize="11px"
                    >
                      {cat}
                    </Box>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          ) : null}

          {hasCapturedDetails ? (
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2} mb={2}>
                {request.clientLabel ? <DetailCell label="Company / client">{request.clientLabel}</DetailCell> : null}
                {request.contactName ? <DetailCell label="Contact name">{request.contactName}</DetailCell> : null}
                {request.contactEmail ? <DetailCell label="Email">{request.contactEmail}</DetailCell> : null}
                {request.contactNumber ? <DetailCell label="Phone">{request.contactNumber}</DetailCell> : null}
                {request.siteAddress ? <DetailCell label="Address / location">{request.siteAddress}</DetailCell> : null}
                {scheduleRange ? <DetailCell label="Preferred schedule">{scheduleRange}</DetailCell> : null}
                {request.contactAvailabilityText
                  && request.preferredScheduleAt
                  && request.contactAvailabilityText !== scheduleRange ? (
                  <DetailCell label="Availability text">{request.contactAvailabilityText}</DetailCell>
                ) : null}
                {request.serialNumber ? <DetailCell label="Serial number">{request.serialNumber}</DetailCell> : null}
                {request.unitModel ? <DetailCell label="Unit model">{request.unitModel}</DetailCell> : null}
            </SimpleGrid>
          ) : (
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2} mb={2}>
              {request.contactName ? <DetailCell label="Contact name">{request.contactName}</DetailCell> : null}
              {request.contactEmail ? <DetailCell label="Email">{request.contactEmail}</DetailCell> : null}
            </SimpleGrid>
          )}

          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2} mb={2}>
            {request.requestSource || request.requestChannel ? (
              <DetailCell label="Source">
                <HStack spacing={2} flexWrap="wrap">
                  {request.requestSource ? (
                    <RequestSourceBadge source={request.requestSource} />
                  ) : null}
                  {request.requestChannel ? (
                    <Text as="span" fontSize="12px">{request.requestChannel}</Text>
                  ) : null}
                </HStack>
              </DetailCell>
            ) : null}
            {request.serviceType ? (
              <DetailCell label="Service">
                {request.serviceParent ? `${request.serviceParent} → ` : ''}
                {request.serviceType}
              </DetailCell>
            ) : null}
            {request.priority || request.ticketStatusLabel ? (
              <DetailCell label="Priority / status">
                {[request.priority, request.ticketStatusLabel].filter(Boolean).join(' · ')}
              </DetailCell>
            ) : null}
          </SimpleGrid>
          {request.submittedAt ? (
            <Text fontSize="11px" color={colors.textMuted}>
              Submitted {formatDate(request.submittedAt)}
            </Text>
          ) : null}
          {request.acknowledgedAt ? (
            <Text fontSize="11px" color={colors.textMuted}>
              Acknowledged {formatDate(request.acknowledgedAt)}
            </Text>
          ) : null}
          {request.attachmentMetadata && request.attachmentMetadata.length > 0 ? (
            <Box>
              <Text fontSize="11px" fontWeight="500" color={colors.textSecondary} mb={1}>
                Attachments
              </Text>
              <VStack align="stretch" spacing={1}>
                {request.attachmentMetadata.map((att, idx) =>
                  att.file_url ? (
                    <Link key={idx} href={att.file_url} isExternal fontSize="12px" color={colors.primary}>
                      {att.file_name}
                    </Link>
                  ) : (
                    <Text key={idx} fontSize="12px" color={colors.text}>
                      {att.file_name}
                    </Text>
                  ),
                )}
              </VStack>
            </Box>
          ) : null}
        </VStack>
      </Collapse>
    </Box>
  )
}
